import type { Order } from '@commercelayer/sdk'
import type { Views } from '@integration-layer/schemas/GAEvents'
import {
  addToCartSchema,
  removeFromCartSchema,
  sameItemCartSchema,
  viewAbandonCartSchema,
  viewCartSchema,
} from '@integration-layer/schemas/GAEvents/cart'

/**
 * Represents the possible cart view types.
 * @typedef {('mini_cart'|'cart_page')} CartViews
 */
type CartViews = Extract<Views, 'mini_cart' | 'cart_page'>

/**
 * Parameters for viewing a cart event.
 * @typedef {Object} ViewCartParams
 * @property {CartViews} view - The type of cart view.
 * @property {Order} [cart] - Optional cart order details.
 */
type ViewCartParams = { view: CartViews; cart?: Order }

/**
 * Parameters for adding an item to cart event.
 * @typedef {Object} AddToCartParams
 * @property {Views} view - The view where the item is added.
 */
type AddToCartParams = { view: Views }

/**
 * Parameters for removing an item from cart event.
 * @typedef {Object} RemoveFromCartParams
 * @property {CartViews} view - The type of cart view.
 * @property {number} [qty] - Optional quantity to remove.
 */
type RemoveFromCartParams = {
  view: CartViews
  qty?: number
}

/**
 * Composable for handling Google Analytics cart-related events.
 * Provides methods to track cart interactions like view, add, remove, and abandon.
 *
 * @returns {Object} Cart event tracking methods and payload
 * @example
 * const {
 *   dispatchViewCartEvent,
 *   dispatchAddToCartEvent,
 *   dispatchRemoveFromCartEvent
 * } = useGACartEvents()
 */
export const useGACartEvents = () => {
  const debug = !!useRuntimeConfig().public.ga4.debug
  const {
    getCartAction,
    getEcommerceData,
    setCartEventsPayload,
    resetCartEventsPayload,
  } = useGAEcommerceData()

  /**
   * Dispatches a view cart event to Google Analytics.
   * Tracks when a user views their cart on different views.
   *
   * @param {ViewCartParams} params - Cart view parameters
   * @param {Object} [options] - Additional event dispatch options
   * @param {boolean} [options.waitForDatalayerSetup=false] - Wait for data layer setup
   * @returns {Promise<void>}
   * @example
   * dispatchViewCartEvent({ view: 'cart_page', cart: myCart })
   */
  const dispatchViewCartEvent = async (
    { view, cart }: ViewCartParams,
    options?: { waitForDatalayerSetup?: boolean }
  ) => {
    if (cart) setCartEventsPayload({ cart })
    const ecommerce = await getEcommerceData()
    const showItemList = computed(() =>
      ecommerce?.items.length === 1 ? ecommerce.items[0].item_list_name : ''
    )

    if (!ecommerce) return
    useGASendEvent(
      viewCartSchema,
      {
        event: 'view_cart',
        eventID: '006',
        ecommerce: {
          item_list_id: '',
          item_list_name: showItemList.value,
          view_type: view,
          ...ecommerce,
        },
      },
      {
        clearEcommerce: true,
        debug,
        waitForDatalayerSetup: options?.waitForDatalayerSetup,
      }
    )
  }

  /**
   * Dispatches an add to cart event to Google Analytics.
   * Tracks when a user adds an item to their cart.
   *
   * @param {AddToCartParams} params - Add to cart parameters
   * @returns {Promise<void>}
   * @example
   * dispatchAddToCartEvent({ view: 'product_page' })
   */
  const dispatchAddToCartEvent = async ({ view }: AddToCartParams) => {
    const cartAction = getCartAction()
    if (cartAction !== 'add' && cartAction !== 'increase') return

    const ecommerce = await getEcommerceData(1)
    if (!ecommerce) return

    useGASendEvent(
      addToCartSchema,
      {
        event: 'add_to_cart',
        eventID: '007',
        ecommerce: {
          view_type: view,
          ...ecommerce,
        },
      },
      { clearEcommerce: true, debug }
    )
    resetCartEventsPayload()
    await dispatchAbandonCartEvent(true)
  }

  /**
   * Dispatches a remove from cart event to Google Analytics.
   * Tracks when a user removes an item from their cart.
   *
   * @param {RemoveFromCartParams} params - Remove from cart parameters
   * @returns {Promise<void>}
   * @example
   * dispatchRemoveFromCartEvent({ view: 'cart_page', qty: 1 })
   */
  const dispatchRemoveFromCartEvent = async ({
    view,
    qty,
  }: RemoveFromCartParams) => {
    const ecommerce = await getEcommerceData(qty)
    if (!ecommerce) return
    useGASendEvent(
      removeFromCartSchema,
      {
        event: 'remove_from_cart',
        eventID: '008',
        ecommerce: {
          view_type: view,
          ...ecommerce,
        },
      },
      { clearEcommerce: true, debug }
    )
    resetCartEventsPayload()
    await dispatchAbandonCartEvent(true)
  }

  /**
   * Dispatches an abandon cart event to Google Analytics.
   * Tracks when a cart is potentially abandoned by the user.
   *
   * @param {boolean} [updateCart=false] - Whether to update cart before dispatching
   * @returns {Promise<void>}
   * @example
   * dispatchAbandonCartEvent(true)
   */
  const dispatchAbandonCartEvent = async (updateCart?: boolean) => {
    const { cart } = useCart()
    if (cart?.value && updateCart) setCartEventsPayload({ cart: cart.value })
    const ecommerce = await getEcommerceData()
    if (!ecommerce) return
    useGASendEvent(viewAbandonCartSchema, {
      event: 'view_abandon_cart',
      ecommerce,
    })

    // Same item_id in the cart with more than one size
    const sizesByItemId = new Map<string, Set<string>>()
    ecommerce.items.forEach(({ item_id, item_size }) => {
      const sizes = sizesByItemId.get(item_id) ?? new Set<string>()
      sizesByItemId.set(item_id, sizes.add(item_size))
    })
    const hasSameItem = [...sizesByItemId.values()].some(
      sizes => sizes.size > 1
    )

    useGASendEvent(sameItemCartSchema, {
      event: 'same_item_cart',
      eventID: 'A018',
      same_item: hasSameItem ? 1 : 0,
    })
  }

  return {
    setCartEventsPayload,
    dispatchViewCartEvent,
    dispatchAddToCartEvent,
    dispatchRemoveFromCartEvent,
    dispatchAbandonCartEvent,
  }
}
